import {FC, useRef, useState} from 'react';
import {Box, Button, Center, Flex, Grid} from '@chakra-ui/react';
import {Svg, TSvgName, QuickMenu, useScreenSize, TBreakpointStyle, Loader, Modal} from 'ui-kit';
import {
  api,
  useT,
  IDocumentInfo,
  getIsTodayShortString,
  extractFileExtension
} from '@kcast-fe/core';
import FileSaver from 'file-saver';

import {IndexCell, StatusCell} from './components';

interface IProps {
  item: IDocumentInfo;
  icon: TSvgName;
  progress: number;
  isUploading: boolean;
  isProcessing: boolean;
  gridTemplate: TBreakpointStyle;
}

const DocumentListItemFC: FC<IProps> = ({
  item,
  icon,
  progress,
  isUploading,
  isProcessing,
  gridTemplate
}) => {
  const deleteMutation = api.documents.useDeleteDocumentMutation();
  const downloadMutation = api.documents.useDocumentDownloadMutation();
  const downloadAsMdMutation = api.documents.useDocumentDownloadAsMdMutation();

  const [isDeletingOpen, setIsDeletingOpen] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const {isLessThan768} = useScreenSize();
  const {t} = useT();

  const isDownloadingOrDeleting = downloadMutation.isPending || deleteMutation.isPending;

  const handleDownload = () => {
    downloadMutation.mutate(item.id, {
      onSuccess: (resp) => {
        FileSaver.saveAs(new Blob([resp]), item.title);
      },
      onError: () => {
        console.error('[File] Download error');
      }
    });
  };

  const handleDownloadAsMd = () => {
    downloadAsMdMutation.mutate(item.id, {
      onSuccess: (resp) => {
        const extension = extractFileExtension(item.title);
        FileSaver.saveAs(new Blob([resp]), item.title.replace(`.${extension}`, '.md'));
      },
      onError: () => {
        console.error('[File] Download error');
      }
    });
  };

  const handleDelete = () => {
    setIsDeletingOpen(false);
    deleteMutation.mutate(item.id);
  };

  return (
    <Box ref={containerRef} position={'relative'}>
      {/* UPLOAD PROGRESS */}
      <Box h={'48px'} w={'100%'} position={'absolute'} zIndex={1} left={0} top={0}>
        {(isUploading || isProcessing) && (
          <Box
            h={'100%'}
            bg={isProcessing ? '{colors.stateSuccess}' : '{colors.textLink}'}
            transition={'all var(--tr-500-ei-eo)'}
            transform={`scaleX(${progress})`}
            transformOrigin={'left center'}
            opacity={0.1}
            data-testid={'upload-progress'}
          />
        )}
      </Box>

      <Grid
        h={'48px'}
        gap={isLessThan768 ? '6px' : '12px'}
        position={'relative'}
        alignItems={'center'}
        borderBottom={'thin solid'}
        borderColor={'{colors.borderDefault}'}
        _hover={{cursor: 'pointer', bg: '{colors.bgSurface}'}}
        transition={'background var(--tr-100-ei)'}
        gridTemplateColumns={gridTemplate}
        className={'group'}
        zIndex={2}
      >
        {/* DOCUMENT ICON */}
        <Center>
          <Svg name={icon} size={'s20'} />
        </Center>

        {/* DOCUMENT TITLE */}
        <Box className={'cut-text'}>{item.title}</Box>

        {/* DOCUMENT STATUS */}
        <StatusCell item={item} />

        {!isLessThan768 && (
          <>
            {/* DOCUMENT INDEX */}
            <IndexCell item={item} />
            {/* DOCUMENT DATE OR STATE */}
            <Box>
              {isUploading && <Box color={'{colors.textLink}'}>{t('labels.uploading')}</Box>}
              {isProcessing && <Box color={'{colors.stateSuccess}'}>{t('labels.processing')}</Box>}
              {!isUploading && !isProcessing && <>{getIsTodayShortString(item.created_at)}</>}
            </Box>
          </>
        )}

        {/* DOCUMENT MENU */}
        <Box
          transition={'opacity var(--tr-150-ei)'}
          data-testid={'dots'}
          opacity={isDownloadingOrDeleting || isLessThan768 ? 1 : 0}
          _groupHover={{opacity: 1}}
        >
          {!isProcessing && !isUploading && !isDownloadingOrDeleting && (
            <QuickMenu
              hideActive
              container={containerRef}
              trigger={<Svg name={'Dots'} size={'s24'} />}
              items={[
                {
                  id: t('actions.download'),
                  name: t('actions.download'),
                  onClick: handleDownload
                },
                {
                  id: t('actions.downloadMd'),
                  name: t('actions.downloadMd'),
                  onClick: handleDownloadAsMd
                },
                {
                  id: t('actions.delete'),
                  name: t('actions.delete'),
                  onClick: () => setIsDeletingOpen(true),
                  isDanger: true
                }
              ]}
            />
          )}

          {isDownloadingOrDeleting && (
            <Box pt={'4px'}>
              <Loader />
            </Box>
          )}
        </Box>
      </Grid>

      {/* DELETION APPROVAL DIALOG */}
      <Modal
        isOpen={isDeletingOpen}
        title={t('labels.fileDeletion')}
        onClose={() => setIsDeletingOpen(false)}
      >
        <Box p={'24px 24px 0 24px'}>{t('messages.fileDeletion', {name: item.title})}</Box>

        <Flex justifyContent={'flex-end'} p={'24px 24px 0 24px'}>
          <Flex gap={'12px'}>
            <Button size={'md'} variant={'subtle'} onClick={() => setIsDeletingOpen(false)}>
              {t('actions.cancel')}
            </Button>

            <Button
              size={'md'}
              /* @ts-ignore */
              variant={'danger'}
              onClick={handleDelete}
              data-testid={'delete-approve'}
            >
              {t('actions.deleteFile')}
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </Box>
  );
};

export const DocumentListItem = DocumentListItemFC;
